export const USE_CASE_PROJECT_ID = 'project-use-case'
export const USE_CASE_PROJECT_NAME =
  'Manufacturing Cost Control & Margin Leakage Use Case'

const USE_CASE_ROUTE_PREFIXES = [
  '/app',
  '/projects',
  '/designer',
  '/auto-insight/bi-report-builder',
  '/governance/hub',
  '/operate',
] as const

const USE_CASE_SETTINGS_VIEWS = new Set([
  'billing',
  'ai-data-use',
  'credential-vault',
])

const QA_PROJECT_MARKERS = [
  'project-e2e-demo',
  'e2e',
  'p5-',
  'pe5-',
  'pe6-',
  'p6-',
  'p7-00',
  'qa',
  'seed_',
  'fixture',
  'promoted-pack-ready',
  'regression',
  'journey-regression',
  'release-gate',
  'release gate',
  'playwright',
  'smoke',
  'test project',
]

export type RouteLocationLike = {
  pathname?: string
  search?: string
  hash?: string
}

export type UseCaseRecoveryContext = {
  actionLabel: 'Open demo project'
  href: string
  reason: 'auth_required' | 'demo_project_unavailable'
  returnUrl: string
}

export const getCurrentRouteLocation = (): RouteLocationLike => {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', hash: '' }
  }

  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  }
}

const parseRouteLocation = (
  route?: string | RouteLocationLike
): {
  pathname: string
  searchParams: URLSearchParams
  hash: string
} => {
  const fallback = getCurrentRouteLocation()
  if (typeof route === 'string') {
    const url = new URL(route, 'https://infergraph.local')
    return {
      pathname: url.pathname,
      searchParams: url.searchParams,
      hash: url.hash,
    }
  }

  const pathname = route?.pathname || fallback.pathname || '/'
  const search = route?.search ?? fallback.search ?? ''
  const hash = route?.hash ?? fallback.hash ?? ''
  return {
    pathname,
    searchParams: new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search
    ),
    hash,
  }
}

const matchesRoutePrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

export const isUseCaseRoute = (
  route?: string | RouteLocationLike
): boolean => {
  const { pathname, searchParams } = parseRouteLocation(route)

  if (
    USE_CASE_ROUTE_PREFIXES.some((prefix) =>
      matchesRoutePrefix(pathname, prefix)
    )
  ) {
    return true
  }

  if (pathname === '/settings' || pathname === '/settings/credential-vault') {
    const view = searchParams.get('view')
    return (
      pathname === '/settings/credential-vault' ||
      Boolean(view && USE_CASE_SETTINGS_VIEWS.has(view))
    )
  }

  return false
}

export const hasUseCaseIntent = (
  route?: string | RouteLocationLike
): boolean => {
  const { searchParams } = parseRouteLocation(route)
  const projectId = searchParams.get('projectId')
  const demoHint = searchParams.get('demo')
  return (
    projectId === USE_CASE_PROJECT_ID ||
    (isUseCaseRoute(route) && (demoHint === '1' || demoHint === 'true'))
  )
}

export const isLikelyQaProject = (
  project:
    | { id: string; name?: string; description?: string }
    | null
    | undefined
): boolean => {
  if (!project || project.id === USE_CASE_PROJECT_ID) return false
  const haystack = [project.id, project.name, project.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return QA_PROJECT_MARKERS.some((marker) => haystack.includes(marker))
}

export const buildUseCaseProjectUrl = (
  route?: string | RouteLocationLike
): string => {
  const { pathname, searchParams, hash } = parseRouteLocation(route)
  searchParams.set('projectId', USE_CASE_PROJECT_ID)
  searchParams.delete('demo')
  const search = searchParams.toString()
  return `${pathname}${search ? `?${search}` : ''}${hash || ''}`
}

export const replaceCurrentRouteWithUseCaseProject = (): void => {
  if (typeof window === 'undefined') return
  const nextUrl = buildUseCaseProjectUrl()
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) {
    window.history.replaceState(window.history.state, '', nextUrl)
  }
}

const getCurrentRouteUrl = (): string => {
  const route = getCurrentRouteLocation()
  return `${route.pathname || '/'}${route.search || ''}${route.hash || ''}`
}

export const buildUseCaseRecovery = (
  reason: UseCaseRecoveryContext['reason']
): UseCaseRecoveryContext => ({
  actionLabel: 'Open demo project',
  href: buildUseCaseProjectUrl(),
  reason,
  returnUrl: getCurrentRouteUrl(),
})

export const getRouteProjectId = (
  route?: string | RouteLocationLike
): string | null => {
  return parseRouteLocation(route).searchParams.get('projectId')
}

// T010 (2026-06-07): Cache the "use case project is inaccessible" signal.
// Without this, every navigation through `/app`, `/projects`, `/designer`,
// etc. re-fires `GET /api/projects/project-use-case` and accumulates 403/404
// lines in the dev console for users (e.g. QA persona rumururu@gmail.com)
// who do not have demo access.
//
// 2026-07-05: backed the flag with sessionStorage so it survives a reload.
//
// R017 E3 (2026-08-08): upgraded to localStorage. sessionStorage 는 탭
// 세션마다 리셋되어 로그인 화면/첫 진입마다 잔존 use-case 딥링크 probe 가
// 한 번씩 404 를 남겼다 (브라우저는 4xx 를 항상 콘솔에 기록). localStorage
// 로 영구 무효화하되, **명시적** `?projectId=project-use-case` 딥링크는
// force 재probe (`forceUseCaseProjectRecheck`) 로 접근권 재확인이 가능해
// 이후 데모 프로젝트가 설치된 환경에서도 복구 동선이 살아 있다. probe 가
// 성공하면 flag 는 해제된다 (ProjectContext 쪽 reset 호출).
const USE_CASE_INACCESSIBLE_KEY = 'infergraph.useCaseProjectInaccessible'
// 구 sessionStorage key 도 같은 이름 — 읽기 시 양쪽 다 본다 (마이그레이션).

const readStoredFlag = (): boolean => {
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(USE_CASE_INACCESSIBLE_KEY) === '1'
    ) {
      return true
    }
  } catch {
    // localStorage unavailable — fall through to sessionStorage.
  }
  try {
    return (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(USE_CASE_INACCESSIBLE_KEY) === '1'
    )
  } catch {
    return false
  }
}

let useCaseProjectInaccessibleForSession = readStoredFlag()

export const markUseCaseProjectInaccessible = (): void => {
  useCaseProjectInaccessibleForSession = true
  try {
    localStorage?.setItem(USE_CASE_INACCESSIBLE_KEY, '1')
  } catch {
    // localStorage unavailable (private mode / SSR) — module flag still holds.
  }
  try {
    sessionStorage?.setItem(USE_CASE_INACCESSIBLE_KEY, '1')
  } catch {
    // ignore — best-effort
  }
}

export const isUseCaseProjectKnownInaccessible = (): boolean =>
  useCaseProjectInaccessibleForSession

// For tests, explicit re-auth flows, and successful force re-probes.
export const resetUseCaseProjectAccessCache = (): void => {
  useCaseProjectInaccessibleForSession = false
  try {
    localStorage?.removeItem(USE_CASE_INACCESSIBLE_KEY)
  } catch {
    // ignore — best-effort clear
  }
  try {
    sessionStorage?.removeItem(USE_CASE_INACCESSIBLE_KEY)
  } catch {
    // ignore — best-effort clear
  }
}
